import React, { useCallback, useState } from 'react';
import { IPurchaseOrder } from 'interfaces/purchaseOrder';
import SingleOrderItem from './singleOrderItem';
import _ from 'lodash';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import { getEmptyItem } from './index';
import { fetchAllSupplierInventoryMappingsForAdmin } from 'services';
import CreateInventoryItemModal from 'components/CreateInventoryItemModal';
import PopulateItemsWithImageModal from 'components/PopulateItemsFromImageModal';
import SelectInventoryItemModal from 'components/SelectInventoryItemModal';

const OrderItems: React.FC<IPurchaseOrder.IOrderItemsProps> = props => {
  const { items, setItems, disabled, selectedSupplier, showAddInventoryItemButton = false } = props;
  const [loading, setLoading] = useState<any>(false);
  const [unfoundItemsFromPopulateImage, setUnfoundItemsFromPopulateImage] = useState<any>([]);
  const [showAddInventoryItemModal, setShowAddInventoryItemModal] = useState(false);
  const [showPopulateWithImageModal, setShowPopulateWithImageModal] = useState(false);
  const [showMatchInventoryItemModal,setShowMatchInventoryItemModal] = useState(false);
  const [selectedUnfoundCreateItemData, setSelectedUnfoundCreateItemData] = useState<any>({});

  const deleteItem = (index: number) => {
    const itemsCopy = _.cloneDeep(items);
    if (itemsCopy.length > 1) {
      itemsCopy.splice(index, 1);
      setItems(itemsCopy);
    } else {
      itemsCopy.splice(index, 1, getEmptyItem());
      setItems(itemsCopy);
    }
  };

  const addItem = () => {
    const itemsCopy = _.cloneDeep(items);
    itemsCopy.push(getEmptyItem());
    setItems(itemsCopy);
  };

  const setItem = (item: IPurchaseOrder.IOrderItemType, index: number) => {
    const itemsCopy = _.cloneDeep(items);
    itemsCopy[index] = item;
    setItems(itemsCopy);
  };

  const getAllSupplierNonParentInventoryMappingsForAdmin = async () => {
    const suggestions = await fetchAllSupplierInventoryMappingsForAdmin(selectedSupplier);
    const sanitizedSuggestions = [] as any;
    suggestions.forEach((suggestion: { inventoryItem: { parentItem: any } }) => {
      if (!suggestion?.inventoryItem?.parentItem) {
        sanitizedSuggestions.push(suggestion);
      }
    });
    return sanitizedSuggestions;
  };

  const throttledFetchAllSupplierInventoryMappingsForAdmin = useCallback(_.throttle(
    getAllSupplierNonParentInventoryMappingsForAdmin, 60000), [selectedSupplier]);

  const createInventoryItem = async (item: any) => {
    removeCreatedUnfoundElementFromList();
    setShowAddInventoryItemModal(false);
    setSelectedUnfoundCreateItemData({});
    setItem({
      ...item,
      id: item?.objectId,
      supplierUnitMapping: item?.supplierInventoryMapping?.supplierUnit,
      producerUnitMapping: item?.unit?.description,
      quantity: 1,
    }, items?.length);
  };

  const onMatchInventoryItem = (item: any)=>{
    removeCreatedUnfoundElementFromList();
    setSelectedUnfoundCreateItemData({});
    setShowMatchInventoryItemModal(false);
    setItem({
      ...item,
      id: item?.id,
      name: item?.displayName,
      supplierUnitMapping: item?.supplierUnitMapping,
      producerUnitMapping: item?.producerUnitMapping,
      quantity: 1,
    }, items?.length);
  };

  const removeCreatedUnfoundElementFromList = () => {
    if (!_.isEmpty(selectedUnfoundCreateItemData)) {
      const updatedUnfoundItemsFromPopulateImage = unfoundItemsFromPopulateImage.filter((unfoundItem: any) => 
        unfoundItem.name !== selectedUnfoundCreateItemData.name);;
      setUnfoundItemsFromPopulateImage(updatedUnfoundItemsFromPopulateImage);
    }
  };

  const handlePopulatedData = async (populatedItems: any[]) => {
    const supplierInventoryMappings = await throttledFetchAllSupplierInventoryMappingsForAdmin();
    const itemsToAdd: any = [];
    const unfoundItemsToAdd: any = [];

    populatedItems.forEach((populatedItem) => {
      const matchingSupplierInventoryMapping = supplierInventoryMappings.find((mapping) =>
        _.get(mapping, 'inventoryItem.objectId') === _.get(populatedItem, 'matchedItem.objectId'));
      if (matchingSupplierInventoryMapping) {
        itemsToAdd.push({
          id: _.get(matchingSupplierInventoryMapping, 'inventoryItem.objectId'),
          name: _.get(matchingSupplierInventoryMapping, 'inventoryItem.name'),
          quantity: _.get(matchingSupplierInventoryMapping, 'inventoryItem.lastQuantityOrdered', 1),
          cost: _.get(matchingSupplierInventoryMapping, 'cost'),
          description: _.get(matchingSupplierInventoryMapping, 'inventoryItem.description'),
          price: _.get(matchingSupplierInventoryMapping, 'inventoryItem.price'),
          supplierUnitMapping: _.get(matchingSupplierInventoryMapping, 'supplierUnit'),
          producerUnitMapping: _.get(matchingSupplierInventoryMapping, 'inventoryItem.unit.description'),
        });
      } else {
        unfoundItemsToAdd.push(populatedItem);
      }
    });

    const sanitizedItems = _.filter(items, (item) => item.id !== '');
    setItems([...sanitizedItems, ...itemsToAdd]);

    setUnfoundItemsFromPopulateImage(_.uniqBy(unfoundItemsToAdd, 'name'));
    setShowPopulateWithImageModal(false);
    setLoading(false);
  };

  const deleteUnfoundItem = (indexToRemove: any) => {
    const updatedUnfoundItemsFromPopulateImage = unfoundItemsFromPopulateImage.filter((_: any, index: any) => index !== indexToRemove);;
    setUnfoundItemsFromPopulateImage(updatedUnfoundItemsFromPopulateImage);
  };

  const getAddInventoryItemButton = () => {
    if (!showAddInventoryItemButton) return null;
    return (
      <div>
        <ActionButton
          isLoading={disabled}
          active={!disabled}
          onClick={() => {
            setShowAddInventoryItemModal(true);
          }}
          customClass='ml-5'
          text={<>Add New Item<span>+</span></>}
        />
        {showAddInventoryItemModal &&
          <CreateInventoryItemModal
            onCancel={() => {
              setShowAddInventoryItemModal(false);
              setSelectedUnfoundCreateItemData({});
            }}
            onCreate={createInventoryItem}
            supplierId={selectedSupplier}
            populatedItemData={selectedUnfoundCreateItemData}
          />}
      </div>
    );
  };

  const getPopulateWithImageButton = () => {
    if (!showAddInventoryItemButton) return null;
    return (
      <div>
        <ActionButton
          isLoading={disabled}
          active={!disabled}
          onClick={() => {
            setShowPopulateWithImageModal(true);
          }}
          customClass='ml-5'
          text={<>Populate With Image<span>+</span></>}
        />
        {showPopulateWithImageModal &&
          <PopulateItemsWithImageModal
            onCancel={() => {
              setShowPopulateWithImageModal(false);
            }}
            setLoading={setLoading}
            loading={loading}
            onSuccess={handlePopulatedData}
            supplierId={selectedSupplier}
          />}
      </div>
    );
  };

  const getDeleteUnfoundItem = (index: any) => (
    <div className={styles['delete-unfound-item']}>
      <i
        onClick={() => deleteUnfoundItem(index)}
        className='fa fa-trash-o'
      />
    </div>
  );

  const getAddNewInventoryItemButton=(item: any)=>(
    <div className={styles['item-create-button']}>
      <ActionButton
        isLoading={disabled}
        active={!disabled}
        customClass='ml-4'
        onClick={() => {
          setSelectedUnfoundCreateItemData({ name: item.name, price: item.cost });
          setShowAddInventoryItemModal(true);
        }}
        text={<>Add New Item<span>+</span></>}
      />
    </div>
  );

  function getMatchInventoryItemModal(){
    return (
      showMatchInventoryItemModal && 
      <SelectInventoryItemModal
        onSelectedItem={onMatchInventoryItem} 
        item={selectedUnfoundCreateItemData}
        selectedSupplier={selectedSupplier}
        showMatchInventoryItemModal={showMatchInventoryItemModal}
        setShowMatchInventoryItemModal={setShowMatchInventoryItemModal}
        fetchDefaultSuggestions={throttledFetchAllSupplierInventoryMappingsForAdmin}
      />
    );
  }

  const getMatchInventoryItemButton =(item: any)=>(
    <div className={styles['item-create-button']}>
      <ActionButton
        isLoading={disabled}
        active={!disabled}
        customClass='ml-4'
        onClick={() => {
          setSelectedUnfoundCreateItemData({ name: item.name, price: item.cost });
          setShowMatchInventoryItemModal(true);
        }}
        text={<>Match Item</>}
      />
    </div>
  );

  const getUnmatchedItems = () => {
    if (unfoundItemsFromPopulateImage.length > 0) {
      return (
        <>
          <h5 className={styles['unmatched-items-header']}>Unmatched Items From Image</h5>
          <div style={{ border: '1px solid #E0E0E0' }}>
            <div className={styles['items']}>
              {unfoundItemsFromPopulateImage.map((item: any, index: number) => (
                <div className='' key={index}>
                  <div className={styles['single-item']}>
                    <div className={styles['item-name']}>
                      <div className={styles['item-number']}>#{index + 1}</div>
                      <div className={styles['label']}>Item Name</div>
                      <div className={styles['details']}>{item.name || '-'}</div>
                    </div>
                    <div className={styles['item-cost']}>
                      <div className={styles['label']}>Quantity</div>
                      <div className={styles['details']}>{item.quantity || 0}</div>
                    </div>
                    <div className={styles['item-cost']}>
                      <div className={styles['label']}>Unit</div>
                      <div className={styles['details']}>{item.unit || '-'}</div>
                    </div>
                    <div className={styles['item-cost']}>
                      <div className={styles['label']}>Cost($)</div>
                      <div className={styles['details']}>{item.cost || 0}</div>
                    </div>
                    {getAddNewInventoryItemButton(item)}
                    {getMatchInventoryItemButton(item)}
                    {getMatchInventoryItemModal()}
                    {getDeleteUnfoundItem(index)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    } else {
      return null;
    }
  };

  return (
    <div className={styles['items-container']}>
      <div className={styles['items']}>
        {items.map((item, i) => (
          <SingleOrderItem
            key={i}
            index={i}
            item={item}
            setItem={setItem}
            disabled={disabled}
            deleteItem={deleteItem}
            selectedSupplier={selectedSupplier}
            fetchDefaultSuggestions={throttledFetchAllSupplierInventoryMappingsForAdmin}
          />
        ))}
      </div>
      {getUnmatchedItems()}
      <div className={styles['button-container']}>
        <ActionButton
          isLoading={disabled}
          active={!disabled}
          onClick={addItem}
          text={<>Entry<span>+</span></>}
        />
        {getAddInventoryItemButton()}
        {getPopulateWithImageButton()}
      </div>
    </div>
  );
};

export default OrderItems;
